import { useRouteQuery } from "@vueuse/router";

/**
 * @returns function to get the redirect path after login based on the previously visited page
 */
export function useRedirectPath() {
  if (import.meta.server) return;

  const { getPreviouslyVisitedPagePath } = useAppStatusStore();
  const localePath = useLocalePath();

  const routeRedirectQuery = useRouteQuery<string | undefined>("redirect", "", {
    transform: (rawValue) => {
      if (!rawValue) return undefined;

      return Array.isArray(rawValue) ? rawValue[0] : rawValue;
    },
  });

  const getRedirectPath = () => {
    if (routeRedirectQuery.value) {
      return localePath(routeRedirectQuery.value);
    }

    // user may have been on one of the below pages before logging in, we don't want to redirect them back to these pages after login.
    const pathsToNotRedirectAfterLogin = [
      "/password-reset",
      "/verify-email",
      "/login",
    ];

    const shouldRedirectToHomepage =
      getPreviouslyVisitedPagePath &&
      pathsToNotRedirectAfterLogin.some((p) =>
        getPreviouslyVisitedPagePath.includes(p),
      );

    // if we don't have a valid redirect state, redirect to homepage
    if (shouldRedirectToHomepage || !getPreviouslyVisitedPagePath) {
      return localePath("/");
    }

    return getPreviouslyVisitedPagePath;
  };

  return {
    routeRedirectQuery,
    getRedirectPath,
  };
}

export function useLogin() {
  const auth = useAuth();
  const connection = useRealtimeConnection();
  const redirect = useRedirectPath();

  const login = async (
    credentials: { email: string; password: string },
    options: {
      redirectAfterLogin?: boolean;
    },
  ) => {
    const optionsWithDefaults = {
      redirectAfterLogin: true,
      ...options,
    };

    await auth.loginWith({
      strategy: "email",
      options: {
        email: credentials.email.toLowerCase(),
        password: credentials.password,
      },
    });

    // Will reconnect on close with the updated user token
    connection?.stop();

    if (optionsWithDefaults.redirectAfterLogin) {
      const path = redirect?.getRedirectPath();
      await navigateTo(path);
    }
  };

  return {
    /**
     * Logs in with the `customStrategy`.
     *
     * Will create a new session if the email and password are provided.
     *
     * Will use the existing session if the tokens are provided
     */
    login,
  };
}
